<template>
  <div v-if="store" class="bg-white rounded-2xl shadow-md p-6">
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
      <StackedField label="Identifiant" :value="String(store.id)" />
      <StackedField label="Réseau">
        <a class="text-primary hover:text-primary-dark" :href="organizationUrl">
          {{ organization.name }}
        </a>
      </StackedField>
      <StackedField v-if="store.billingEntity" label="Entité de facturation">
        <router-link :to="`/billing-entities/${store.billingEntity.id}`" class="text-primary hover:text-primary-dark">
          {{ store.billingEntity.name }}
        </router-link>
      </StackedField>
      <CopyableFieldRow
        v-if="store.publicSubscriptionLink"
        label="Souscription publique"
        :value="store.publicSubscriptionLink"
      />
      <StackedField
        v-if="store.mandate"
        label="Mandat de prélèvement"
        :value="$t(`mandate.status.${mandate.status}`)"
      />
      <StackedField
        v-if="mandate?.responsibleEmail"
        :label="$t('mandate.responsible_email')"
        :value="mandate.responsibleEmail"
      />
      <StackedField v-if="mandateToSetup" label="Lien d'activation du mandat">
        <a :href="mandate.authorizationUrl" target="_blank" rel="noopener noreferrer" class="text-blue-400 underline">
          Lien
        </a>
      </StackedField>
      <StackedField v-if="store.shop" label="Site web">
        <a class="text-primary hover:text-primary-dark" target="_blank" rel="noopener noreferrer" :href="store.shop">
          Lien
        </a>
      </StackedField>
      <StackedField label="Catégorie">
        <Selector
          size="small"
          class="w-48"
          id="category"
          name="category"
          v-model="category"
          :options="categoryOptions"
        />
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Potentiel de ventes">
        <div class="flex items-center">
          <div v-if="isEditingMonthlyTarget" class="flex items-center">
            <input
              type="number"
              v-model="monthlySalesTargetInternalValue"
              class="shadow-xs focus:ring-primary focus:border-primary block w-32 sm:text-sm border-gray-300 rounded-md mr-2"
            />
            <SaveIcon class="h-5 cursor-pointer text-primary-dark" @click="saveMonthlySalesTarget" />
          </div>
          <div v-else class="flex items-center">
            <span class="mr-2">{{ monthlySalesTargetInternalValue }}</span>
            <PencilIcon
              class="h-5 cursor-pointer text-gray-400 hover:text-gray-600"
              @click="isEditingMonthlyTarget = true"
            />
          </div>
        </div>
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Split challenge vendeur">
        <div class="flex items-center">
          <div v-if="isEditingChallengeSellerCommissionRatio" class="flex items-center">
            <input
              type="number"
              v-model="challengeSellerCommissionRatioInternalValue"
              class="shadow-xs focus:ring-primary focus:border-primary block w-32 sm:text-sm border-gray-300 rounded-md mr-2"
            />
            <SaveIcon class="h-5 cursor-pointer text-primary-dark" @click="saveChallengeSellerCommissionRatio" />
          </div>
          <div v-else class="flex items-center">
            <span class="mr-2">{{ challengeSellerCommissionRatioInternalValue }}</span>
            <PencilIcon
              class="h-5 cursor-pointer text-gray-400 hover:text-gray-600"
              @click="isEditingChallengeSellerCommissionRatio = true"
            />
          </div>
        </div>
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Région">
        <Selector size="small" class="w-48" id="region" name="region" v-model="region" :options="getRegions()" />
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Paiement des primes">
        <Selector
          size="small"
          class="w-48"
          id="retributionsPayoutMethod"
          name="retributionsPayoutMethod"
          v-model="retributionsPayoutMethod"
          :options="retributionsPayoutMethodsOptions"
        />
      </StackedField>
      <StackedField v-if="isEstalyGiftCard" label="Fournisseur carte cadeau">
        <Selector
          size="small"
          class="w-48"
          id="giftCardProvider"
          name="giftCardProvider"
          v-model="giftCardProvider"
          :options="giftCardProvidersOptions"
        />
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Booster">
        <Selector
          size="small"
          class="w-48"
          id="retributionsBoostEnabled"
          name="retributionsBoostEnabled"
          v-model="retributionsBoostEnabled"
          :options="retributionsBoostEnabledOptions"
        />
      </StackedField>
      <StackedField v-if="isStoreOffline(store)" label="Enregistrer une vente">
        <Selector
          size="small"
          class="w-48"
          id="recordASale"
          name="recordASale"
          v-model="recordASale"
          :options="recordASaleOptions"
        />
      </StackedField>
      <StackedField v-if="isStoreActive && isStoreOffline(store)" label="Date de formation">
        <SecondaryButton @click="editOnboardingDateModalOpen = true">
          <template v-if="store.onboardingDate || onboardingDate">
            {{ formatOnboardingDate(store.onboardingDate || onboardingDate) }}
          </template>
          <template v-else>Configurer</template>
        </SecondaryButton>
      </StackedField>
    </div>

    <Modal :open="editOnboardingDateModalOpen" @close="closeOnboardingDateModal">
      <FormField
        id="onboarding_date"
        name="onboarding_date"
        label="Configurer la date de formation"
        type="date"
        v-model="onboardingDate"
      />
      <div class="flex justify-end mt-4">
        <SecondaryButton @click="confirmOnboardingDate">Confirmer</SecondaryButton>
      </div>
    </Modal>
  </div>
</template>

<script>
import { PencilIcon, SaveIcon } from "@heroicons/vue/outline";
import { CopyableFieldRow, Selector, Modal, SecondaryButton, FormField, StackedField } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";
import { format } from "date-fns";

export default {
  components: {
    CopyableFieldRow,
    PencilIcon,
    SaveIcon,
    Selector,
    SecondaryButton,
    Modal,
    FormField,
    StackedField,
  },

  props: ["store"],

  data() {
    return {
      editOnboardingDateModalOpen: false,
      onboardingDate: this.store.onboardingDate,
      regionInternalValue: this.store.region,
      categoryInternalValue: this.store.category,
      retributionsPayoutMethodInternalValue: this.store.retributionsPayoutMethod,
      giftCardProviderInternalValue: this.store.giftCardProvider,
      retributionsBoostEnabledInternalValue: this.store.retributionsBoostEnabled,
      monthlySalesTargetInternalValue: this.store.monthlySalesTarget,
      challengeSellerCommissionRatioInternalValue: this.store.challengeSellerCommissionRatio,
      recordASaleInternalValue: this.store.storeFeatureConfiguration?.recordASale,
      isEditingMonthlyTarget: false,
      isEditingChallengeSellerCommissionRatio: false,
    };
  },

  computed: {
    ...mapGetters("stores", [
      "getRegions",
      "getCategories",
      "getRetributionPayoutMethods",
      "getGiftCardProviders",
      "isStoreOffline",
    ]),
    ...mapGetters("mandates", ["isMandateToSetup"]),

    organization() {
      return this.store.organization;
    },

    organizationUrl() {
      return `/organizations/${this.organization.id}`;
    },

    isStoreSetup() {
      return this.store.status === "setup";
    },
    isStoreActive() {
      return this.store.status === "active";
    },
    mandate() {
      return this.store.mandate;
    },
    mandateToSetup() {
      return this.isMandateToSetup(this.mandate);
    },
    region: {
      get() {
        return this.regionInternalValue || this.store.region;
      },
      async set(region) {
        const success = await this.updateStore({ region: region });
        if (success) this.regionInternalValue = region;
      },
    },
    category: {
      get() {
        return this.categoryInternalValue || this.store.category;
      },
      async set(category) {
        const success = await this.updateStore({ category: category });
        if (success) this.categoryInternalValue = category;
      },
    },
    retributionsPayoutMethod: {
      get() {
        return this.retributionsPayoutMethodInternalValue || this.store.retributionsPayoutMethod;
      },
      async set(retributionsPayoutMethod) {
        const data = { retributionsPayoutMethod };
        if (retributionsPayoutMethod === "estaly_gift_card") {
          data.giftCardProvider = this.giftCardProviderInternalValue || "edenred";
        } else {
          data.giftCardProvider = null;
        }
        const success = await this.updateStore(data);
        if (success) {
          this.retributionsPayoutMethodInternalValue = retributionsPayoutMethod;
          this.giftCardProviderInternalValue = data.giftCardProvider;
        }
      },
    },
    retributionsBoostEnabled: {
      get() {
        return this.retributionsBoostEnabledInternalValue || this.store.retributionsBoostEnabled;
      },
      async set(retributionsBoostEnabled) {
        const success = await this.updateStore({ retributionsBoostEnabled: retributionsBoostEnabled });
        if (success) this.retributionsBoostEnabledInternalValue = retributionsBoostEnabled;
      },
    },
    isEstalyGiftCard() {
      return this.isStoreOffline(this.store) && this.retributionsPayoutMethod === "estaly_gift_card";
    },
    retributionsPayoutMethodsOptions() {
      return this.getRetributionPayoutMethods().map((method) => ({
        value: method,
        label: this.$t(`store.retributions_payout_method.${method}`),
      }));
    },
    giftCardProvider: {
      get() {
        return this.giftCardProviderInternalValue || this.store.giftCardProvider;
      },
      async set(giftCardProvider) {
        const success = await this.updateStore({ giftCardProvider });
        if (success) this.giftCardProviderInternalValue = giftCardProvider;
      },
    },
    giftCardProvidersOptions() {
      return this.getGiftCardProviders().map((provider) => ({
        value: provider,
        label: this.$t(`store.gift_card_provider.${provider}`),
      }));
    },
    recordASale: {
      get() {
        return this.recordASaleInternalValue ?? this.store.storeFeatureConfiguration?.recordASale;
      },
      async set(value) {
        const success = await this.updateStore({ recordASale: value });
        if (success) this.recordASaleInternalValue = value;
      },
    },
    recordASaleOptions() {
      return [
        { value: true, label: "Activé" },
        { value: false, label: "Désactivé" },
      ];
    },
    retributionsBoostEnabledOptions() {
      return [
        { value: true, label: "Activé" },
        { value: false, label: "Désactivé" },
      ];
    },
    categoryOptions() {
      return this.getCategories().map((category) => ({
        value: category,
        label: category,
      }));
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("stores", ["update"]),

    closeOnboardingDateModal() {
      this.onboardingDate = this.store.onboardingDate;
      this.editOnboardingDateModalOpen = false;
    },

    async confirmOnboardingDate() {
      if (!this.onboardingDate) return;

      const success = await this.updateStore({ onboardingDate: this.onboardingDate });
      if (success) this.editOnboardingDateModalOpen = false;
    },

    formatOnboardingDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },

    async saveMonthlySalesTarget() {
      const success = await this.updateStore({
        monthlySalesTarget: this.monthlySalesTargetInternalValue,
      });
      if (success) this.isEditingMonthlyTarget = !this.isEditingMonthlyTarget;
    },

    async saveChallengeSellerCommissionRatio() {
      const success = await this.updateStore({
        challengeSellerCommissionRatio: this.challengeSellerCommissionRatioInternalValue,
      });
      if (success) this.isEditingChallengeSellerCommissionRatio = !this.isEditingChallengeSellerCommissionRatio;
    },

    async updateStore(data) {
      const { success } = await this.update({ storeId: this.store.id, data });
      if (success) {
        await this.notify({
          category: "simple",
          type: "success",
          title: "Mise à jour effectuée",
        });
        this.$emit("storeUpdated");
      }
      return success;
    },
  },
};
</script>
